import { InputField } from "@/components/input/InputField";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { RoomMeasurementKey } from "@rebuild/shared";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";

export interface MetricProps {
  value: number;
  type: string;
  icon: ReactNode;
  label: string;
  category: RoomMeasurementKey;
  onUpdateMeasurement?: (category: RoomMeasurementKey, value: string) => void;
}

export const Metric = ({ value, type, icon, label, category, onUpdateMeasurement }: MetricProps) => {
  const [edit, setEdit] = useState(false);
  const [inputValue, setInputValue] = useState(String(value));

  const toggleEdit = useCallback(() => {
    setEdit((prev) => !prev);
  }, []);

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (+val !== value) {
        onUpdateMeasurement?.(category, val);
      }
      toggleEdit();
    },
    [category, onUpdateMeasurement, toggleEdit, value],
  );

  return (
    <div className="flex shrink-0 items-center gap-1 px-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center">{icon}</span>
          </TooltipTrigger>
          <TooltipContent className="bg-background text-foreground">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {!edit ? (
        <div
          onClick={() => {
            setInputValue(String(value));
            toggleEdit();
          }}
          className="flex cursor-pointer flex-row items-center space-x-1 p-2"
        >
          <p>{value} </p>
          <p className="text-[#71717A]">{type}</p>
        </div>
      ) : (
        <div style={{ width: `${Math.max(4, inputValue.length + 2)}ch` }}>
          <InputField
            autoFocus
            name="measurement"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={onBlur}
            inputClassName="w-full h-6 text-xs !px-2 !py-0"
          />
        </div>
      )}
    </div>
  );
};
